'use client';

import { useRouter } from 'next/navigation';
import * as React from 'react';

import { AppShell } from '@/components/layout/app-shell';
import { OperatorEventToasts } from '@/components/notifications/operator-event-toasts';
import { useSessionStore } from '@/lib/auth/session';

/**
 * Auth gate for the whole app shell. We wait for the session store to finish
 * rehydrating from localStorage BEFORE deciding whether to redirect — without
 * this, a full page reload / direct URL / bookmark sees the pre-hydration
 * empty state and bounces an authenticated operator to /sign-in.
 *
 * Children (the pages) only mount once hydrated AND authenticated, so their
 * own per-page guards never observe the transient null token.
 */
export default function ShelledLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const hydrated = useSessionStore((s) => s.hasHydrated);
  const hasToken = useSessionStore((s) => s.accessToken !== null);

  React.useEffect(() => {
    if (hydrated && !hasToken) {
      router.replace('/sign-in');
    }
  }, [hydrated, hasToken, router]);

  if (!hydrated) {
    return (
      <div className="grid min-h-dvh place-items-center bg-bg text-fg-muted">
        <span className="text-2xs text-fg-subtle">Loading…</span>
      </div>
    );
  }
  if (!hasToken) {
    return null; // redirecting to /sign-in
  }
  return (
    <AppShell>
      {children}
      <OperatorEventToasts />
    </AppShell>
  );
}
